import React, { useState, useEffect, useCallback } from "react";
import {
  AlertColor,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoopIcon from "@mui/icons-material/Loop";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/store/Store";
import MWExceptionList from "../../../component/MWExceptionList";
import {
  GetPatientCaseNoteListBody,
  NoteList,
} from "../../../redux/Notes/PatientCaseNoteList/Model";
import dayjs from "dayjs";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import { getPatientCaseNoteList } from "../../../redux/Notes/PatientCaseNoteList/API";
import MWSnackbar from "../../../component/MWSnackbar";
import {
  clearNoteAPIRes,
  updateNote,
} from "../../../redux/Notes/updateNoteStatus/API";
import { AddNoteBody } from "../../../redux/Notes/addNotes/Model";
import { addNote, clearAddNoteAPIRes } from "../../../redux/Notes/addNotes/API";
import AdminLoader from "../../AdminLoader";
import { t } from "i18next";
type Props = {
  dialogStatus?: boolean | any;
  activeNoteLoader?: boolean | any;
  dialogUpdateState?: boolean | any;
};

export default function ActivePatientCaseNotes({
  dialogStatus,
  activeNoteLoader,
  dialogUpdateState,
}: Props) {
  const { patientid } = useParams() as {
    patientid: string;
  };
  const { caseid } = useParams() as {
    caseid: string;
  };
  const dispatch = useDispatch();

  const [notes, setNotes] = useState("");
  const handleNotesChange = useCallback((event: any) => {
    setNotes(event.target.value);
  }, []);

  const [notesError, setNotesError] = useState("");
  const notesErrorDiv = notesError ? (
    <Typography>{notesError}</Typography>
  ) : null;

  const [openDialog, setOpenDialog] = useState(false);
  useEffect(() => {
    if (dialogStatus !== undefined) {
      setOpenDialog(dialogStatus);
    }
  }, [dialogStatus]);
  const closeDialog = () => {
    setOpenDialog(false);
    dialogUpdateState(false);
    setNotesError("");
    dispatch(clearAddNoteAPIRes());
  };

  const getDrNoteListBody = {
    patientId: patientid,
    caseId: caseid,
    isActive: true,
  } as GetPatientCaseNoteListBody;

  const addDrNoteListBody = {
    patientId: patientid,
    caseId: caseid,
    note: notes,
  } as AddNoteBody;

  // Active Notes List Api Value from store
  const NoteListFromAPI = useSelector(
    (state: AppState) => state.patientCaseNoteListRes
  );
  const [adminNoteList, setAdminNoteList] = useState([] as any[]);
  const [apiRes, setAPIRes] = useState("");
  const [snackBarType, setSnackBarType] = useState<AlertColor>("success");
  useEffect(() => {
    activeNoteLoader(NoteListFromAPI?.loading);
    if (NoteListFromAPI?.errorMsg !== "") {
      setAPIRes(NoteListFromAPI?.errorMsg);
      setSnackBarType("error");
    }
    if (NoteListFromAPI?.getPatientCaseNoteListRes?.noteList !== undefined) {
      let noteListItem =
        NoteListFromAPI?.getPatientCaseNoteListRes?.noteList.map(
          (element: NoteList) => {
            return {
              id: element.id,
              note: element?.note !== undefined ? element?.note : "",
              createdDate:
                element?.createdDate !== undefined
                  ? dayjs(element?.createdDate).format("ddd, DD/MM/YYYY")
                  : "",
              createBy:
                element?.createdBy === undefined
                  ? ""
                  : element?.createdBy?.user?.first_name +
                    " " +
                    element?.createdBy?.user?.last_name,
              modifiedDate:
                element?.modifiedDate !== undefined
                  ? dayjs(element?.modifiedDate).format("ddd, DD/MM/YYYY")
                  : "",
              modifiedBy:
                element?.modifiedBy === undefined
                  ? ""
                  : element?.modifiedBy?.user?.first_name +
                    " " +
                    element?.modifiedBy?.user?.last_name,
            };
          }
        );
      setAdminNoteList(noteListItem);
    } else setAdminNoteList([] as any[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [NoteListFromAPI]);

  // Add Notes Api Value from store
  const addNotesVal = useSelector((state: AppState) => state.addNoteRes);
  useEffect(() => {
    console.log(NoteListFromAPI);
    if (addNotesVal?.errorMsg !== "") {
      setAPIRes(addNotesVal?.errorMsg);
      setSnackBarType("error");
    } else if (addNotesVal?.successMsg !== "") {
      setAPIRes(addNotesVal?.successMsg);
      setSnackBarType("success");
      closeDialog();
      dispatch(getPatientCaseNoteList(getDrNoteListBody));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNotesVal]);

  const columns: GridColDef[] = [
    { field: "note", headerName: `${t("common.note", { ns: ["home"] })!}`, flex: 1 },
    { field: "createdDate", headerName: `${t("admindashboardpage.createDate", { ns: ["home"] })!}`, minWidth: 140 },
    {
      field: "createBy",
      headerName: `${t("common.createdBy", { ns: ["home"] })!}`,
      flex: 1,
    },
    { field: "modifiedBy", headerName: `${t("common.modifyBy", { ns: ["home"] })!}`, flex: 1 },
    { field: "modifiedDate", headerName: `${t("appointmentpage.modifieddate", { ns: ["home"] })!}`, minWidth: 140 },
    {
      field: "id",
      headerName: `${t("docnotelist.modifiedstatus", { ns: ["home"] })!}`,
      minWidth: 35,
      renderCell: (params: GridCellParams) => (
        <Button
          startIcon={<LoopIcon />}
          variant="outlined"
          onClick={() => {
            toggleNoteStatus(params.value);
          }}
          sx={{ textTransform: "none" }}
          disabled={updateNotesVal?.loading}
        >
          {t("userprofilepage.edit", { ns: ["home"] })!}
        </Button>
      ),
    },
  ];

  // Add notes with patient & case Id details
  const addNotes = () => {
    if (notes === "") {
      setNotesError(`${t("common.pleaseAddNote", { ns: ["home"] })!}`);
    } else {
      dispatch(addNote(addDrNoteListBody));
    }
  };

  // Notes Status change API
  function toggleNoteStatus(response: number) {
    dispatch(updateNote(response));
  }
  // update Notes Api Value from store
  const updateNotesVal = useSelector((state: AppState) => state.updateNoteRes);
  useEffect(() => {
    activeNoteLoader(updateNotesVal?.loading);
    if (updateNotesVal?.errorMsg !== "") {
      setAPIRes(updateNotesVal?.errorMsg);
      setSnackBarType("error");
    } else if (updateNotesVal?.updateNoteResRes?.message !== undefined) {
      setAPIRes(updateNotesVal?.updateNoteResRes?.message);
      dispatch(getPatientCaseNoteList(getDrNoteListBody));
      setSnackBarType("success");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNotesVal]);

  useEffect(() => {
    dispatch(getPatientCaseNoteList(getDrNoteListBody));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const checkAlertClose = (passedVal: boolean) => {
    if (passedVal) {
      dispatch(clearAddNoteAPIRes());
      dispatch(clearNoteAPIRes());
    }
  };

  // Submit note modal
  const NotesModalMarkup = (
    <Dialog open={openDialog} onClose={closeDialog} fullWidth>
      {addNotesVal?.loading ? <AdminLoader /> : null}
      <DialogTitle>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Grid item>
            <Typography variant="h6">{t("common.addNote", { ns: ["home"] })!}</Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={closeDialog}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <TextField
          value={notes}
          label={t("filePrepPage.patientNotes", { ns: ["home"] })}
          placeholder={t("docnotelist.placeholder", { ns: ["home"] })!}
          id="notesID"
          onChange={handleNotesChange}
          fullWidth
          rows={4}
          maxRows={4}
        />
        <Typography sx={{ color: "#c62828" }}>{notesErrorDiv}</Typography>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
          spacing={1}
        >
          <Grid item>
            <Button
              variant="outlined"
              onClick={closeDialog}
              sx={{ textTransform: "none" }}
            >
              {t("accountdetail.cancel", { ns: ["home"] })!}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={addNotes}
              disabled={addNotesVal?.loading}
              sx={{ textTransform: "none" }}
            >
              {t("Workschedulepage.submit", { ns: ["home"] })!}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      {adminNoteList.length > 0 ? (
        <DataGrid
          rows={adminNoteList}
          columns={columns}
          pageSize={10}
          hideFooter
          hideFooterPagination
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          autoHeight
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 250 },
            },
          }}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
        />
      ) : (
        <MWExceptionList />
      )}
      {NotesModalMarkup}
      <MWSnackbar
        msg={apiRes}
        type={snackBarType}
        alertClose={checkAlertClose}
      />
    </Box>
  );
}
